import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Store, ShoppingBag, ArrowLeft, MapPin, Tag, User, MessageCircle, Star, Package, Calendar, Bell, BellOff, Loader2 } from "lucide-react";
import { getShop, getShopListings, resolveImageUrl, followShop, unfollowShop, getShopFollowStatus } from "@/lib/api";
import { toast } from "sonner";
import DOMPurify from "dompurify";

function ListingCard({ listing, navigate }) {
  const isSold = listing.status === "sold";
  
  return (
    <Card 
      className={`bg-white shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer group h-full flex flex-col ${isSold ? 'opacity-75' : ''} border border-[#E5E0D5]`}
      onClick={() => navigate(`/listing/${listing.listing_id}`)}
      data-testid={`shop-listing-card-${listing.listing_id}`}
    >
      <div className="relative w-full" style={{ paddingBottom: '125%' }}>
        {listing.image_url ? (
          <img 
            src={resolveImageUrl(listing.image_url)} 
            alt={listing.title} 
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
          />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-[#F0EBE0] flex items-center justify-center">
            <ShoppingBag className="w-12 h-12 text-[#E5E0D5]" />
          </div>
        )}
        {isSold && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-[var(--liturgical-primary)] text-white px-4 py-1 rounded-full font-['Nunito'] font-semibold text-sm">SOLD</span>
          </div>
        )}
      </div>
      
      <CardContent className="p-3 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26] leading-tight line-clamp-1 flex-1">{listing.title}</h3>
          <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)] text-base whitespace-nowrap">${listing.price}</span>
        </div>
        {listing.average_rating > 0 && (
          <div className="flex items-center gap-1 mb-1" data-testid={`shop-listing-rating-${listing.listing_id}`}>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className={`w-3 h-3 ${s <= Math.round(listing.average_rating) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#E5E0D5]'}`} />
              ))}
            </div>
            <span className="text-[10px] text-[#6B665E] font-['Nunito']">({listing.review_count})</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-auto pt-2">
          <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-[9px] font-['Nunito'] px-1.5 py-0">
            <Tag className="w-2 h-2 mr-0.5" />{listing.category}
          </Badge>
          {listing.condition && (
            <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-[9px] font-['Nunito'] px-1.5 py-0">
              {listing.condition === "new" ? "New" : listing.condition === "like_new" ? "Like New" : listing.condition}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ShopPage({ user }) {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [total, setTotal] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);

  useEffect(() => {
    loadShopData();
  }, [shopId, sort]);

  const loadShopData = async () => {
    setLoading(true);
    try {
      const [shopRes, listingsRes] = await Promise.all([
        getShop(shopId),
        getShopListings(shopId, { sort, limit: 50 })
      ]);
      setShop(shopRes.data);
      setListings(listingsRes.data.listings);
      setTotal(listingsRes.data.total);
      
      // Load follow status if user is logged in
      if (user) {
        try {
          const followRes = await getShopFollowStatus(shopRes.data.owner_id);
          setIsFollowing(followRes.data.is_following);
          setFollowersCount(followRes.data.followers_count);
        } catch {}
      }
    } catch (err) {
      toast.error("Failed to load shop");
      navigate("/marketplace");
    }
    setLoading(false);
  };

  const handleFollowShop = async () => {
    if (!user) {
      toast.error("Please login to follow shops");
      return;
    }
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await unfollowShop(shop.owner_id);
        setIsFollowing(false);
        setFollowersCount(prev => Math.max(0, prev - 1));
        toast.success(`Unfollowed ${shop.name}`);
      } else {
        await followShop(shop.owner_id);
        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
        toast.success(`Now following ${shop.name}! You'll be notified of new listings.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update follow status");
    }
    setFollowLoading(false);
  };

  const isOwner = user?.user_id === shop?.owner_id;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-[#6B665E] font-['Nunito']">Loading shop...</div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-center">
          <Store className="w-16 h-16 text-[#E5E0D5] mx-auto mb-4" />
          <p className="text-[#6B665E] font-['Nunito']">Shop not found</p>
          <Button onClick={() => navigate("/marketplace")} className="mt-4">
            Back to Marketplace
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="shop-page">
      {/* Banner */}
      <div className="relative h-48 md:h-64 bg-gradient-to-r from-[var(--liturgical-primary)] to-[var(--liturgical-secondary)]">
        {shop.banner_url && (
          <img 
            src={resolveImageUrl(shop.banner_url)} 
            alt="Shop banner" 
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/30" />
        
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/marketplace")}
          className="absolute top-4 left-4 text-white hover:bg-white/20"
          data-testid="shop-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Marketplace
        </Button>
        
        {/* Edit button for owner */}
        {isOwner && (
          <Button
            onClick={() => navigate("/my-shop")}
            className="absolute top-4 right-4 bg-white text-[#2D2A26] hover:bg-white/90"
            data-testid="shop-edit-btn"
          >
            Edit Shop
          </Button>
        )}
      </div>

      {/* Shop Info */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-16 relative z-10">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Logo */}
            <div className="flex-shrink-0">
              {shop.logo_url ? (
                <img 
                  src={resolveImageUrl(shop.logo_url)} 
                  alt={shop.name} 
                  className="w-24 h-24 md:w-32 md:h-32 rounded-xl object-cover border-4 border-white shadow-md"
                />
              ) : (
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl bg-[#F0EBE0] flex items-center justify-center border-4 border-white shadow-md">
                  <Store className="w-12 h-12 text-[var(--liturgical-primary)]" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="font-['Crimson_Text'] text-3xl md:text-4xl font-bold text-[#2D2A26] mb-2" data-testid="shop-name">
                {shop.name}
              </h1>
              {shop.tagline && (
                <p className="text-[#6B665E] font-['Nunito'] text-lg mb-3">{shop.tagline}</p>
              )}
              
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-[var(--liturgical-primary)]" />
                  <span className="text-sm text-[#6B665E] font-['Nunito']">
                    <strong className="text-[#2D2A26]">{shop.listing_count || 0}</strong> listings
                  </span>
                </div>
                {shop.rating > 0 && (
                  <div className="flex items-center gap-1" data-testid="shop-rating">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`w-4 h-4 ${s <= Math.round(shop.rating) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#E5E0D5]'}`} />
                      ))}
                    </div>
                    <span className="text-sm text-[#6B665E] font-['Nunito']">
                      <strong className="text-[#2D2A26]">{shop.rating.toFixed(1)}</strong> ({shop.review_count} reviews)
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#6B665E]" />
                  <span className="text-sm text-[#6B665E] font-['Nunito']">
                    Joined {new Date(shop.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </div>

              {shop.description && (
                <div 
                  className="prose prose-sm max-w-none text-[#6B665E] font-['Nunito']"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(shop.description) }}
                />
              )}
            </div>

            {/* Owner Info & Actions */}
            <div className="flex flex-col items-center md:items-end gap-3">
              {shop.owner && (
                <div 
                  className="flex items-center gap-3 p-3 bg-[#F9F7F2] rounded-lg cursor-pointer hover:bg-[#F0EBE0] transition-colors"
                  onClick={() => navigate(`/profile/${shop.owner.user_id}`)}
                  data-testid="shop-owner-link"
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={resolveImageUrl(shop.owner.profile_picture)} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white font-['Nunito']">
                      {shop.owner.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-xs text-[#6B665E] font-['Nunito']">Seller</p>
                    <p className="text-sm font-semibold text-[#2D2A26] font-['Nunito']">{shop.owner.name}</p>
                  </div>
                </div>
              )}
              
              {!isOwner && shop.owner && (
                <>
                  <Button
                    onClick={handleFollowShop}
                    disabled={followLoading}
                    variant={isFollowing ? "outline" : "default"}
                    className={`${isFollowing ? 'border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)]' : 'bg-[var(--liturgical-primary)] hover:brightness-90 text-white'}`}
                    data-testid="shop-follow-btn"
                  >
                    {followLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : isFollowing ? (
                      <BellOff className="w-4 h-4 mr-2" />
                    ) : (
                      <Bell className="w-4 h-4 mr-2" />
                    )}
                    {isFollowing ? "Following" : "Follow Shop"}
                  </Button>
                  <Button
                    onClick={() => navigate(`/messages?to=${shop.owner.user_id}`)}
                    variant="outline"
                    className="border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10"
                    data-testid="shop-message-seller-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" /> Message Seller
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Listings Section */}
        <div className="pb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Crimson_Text'] text-2xl font-semibold text-[#2D2A26]">
              Shop Listings ({total})
            </h2>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-40 border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="shop-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E5E0D5]">
                <SelectItem value="newest" className="font-['Nunito'] text-sm">Newest First</SelectItem>
                <SelectItem value="oldest" className="font-['Nunito'] text-sm">Oldest First</SelectItem>
                <SelectItem value="price_low" className="font-['Nunito'] text-sm">Price: Low to High</SelectItem>
                <SelectItem value="price_high" className="font-['Nunito'] text-sm">Price: High to Low</SelectItem>
                <SelectItem value="title" className="font-['Nunito'] text-sm">Alphabetical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {listings.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-[#E5E0D5]">
              <ShoppingBag className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
              <p className="text-[#6B665E] font-['Nunito']">No listings in this shop yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {listings.map(listing => (
                <ListingCard key={listing.listing_id} listing={listing} navigate={navigate} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
